import { useMemo, useState } from "react";
import { ArrowUpDown, X } from "lucide-react";
import { formatDate, formatCurrency } from "@/lib/utils";

export type Activation = {
  stripe_subscription_id: string;
  stripe_customer_id: string;
  agency_name: string | null;
  ae: string | null;
  csm: string | null;
  quantity: number;
  mrr_cents: number;
  total_mrr_cents: number;
  activated_at: string;
};

type SortField = "activated_at" | "quantity" | "mrr_cents" | "total_mrr_cents" | "upsell_pct";
type SortDir = "asc" | "desc";
type FilterField = "ae" | "csm";

const STRIPE_DASHBOARD = "https://dashboard.stripe.com";

function customerUrl(customerId: string): string {
  return `${STRIPE_DASHBOARD}/customers/${customerId}`;
}

export function ActivationsTable({
  activations,
}: {
  activations: Activation[];
}) {
  const [sortField, setSortField] = useState<SortField>("activated_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [filterField, setFilterField] = useState<FilterField | null>(null);
  const [filterValue, setFilterValue] = useState<string | null>(null);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const applyFilter = (field: FilterField, value: string) => {
    setFilterField(field);
    setFilterValue(value);
  };

  const clearFilter = () => {
    setFilterField(null);
    setFilterValue(null);
  };

  const filtered = useMemo(() => {
    if (!filterField || !filterValue) return activations;
    return activations.filter((a) => a[filterField] === filterValue);
  }, [activations, filterField, filterValue]);

  const upsellPct = (a: Activation): number => {
    const base = a.total_mrr_cents - a.mrr_cents;
    if (base <= 0) return 0;
    return a.total_mrr_cents / base;
  };

  const sorted = useMemo(() => {
    const copy = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    copy.sort((a, b) => {
      if (sortField === "activated_at") {
        return (
          dir *
          (new Date(a.activated_at).getTime() -
            new Date(b.activated_at).getTime())
        );
      }
      if (sortField === "upsell_pct") {
        return dir * (upsellPct(a) - upsellPct(b));
      }
      return dir * (a[sortField] - b[sortField]);
    });
    return copy;
  }, [filtered, sortField, sortDir]);

  const sortIndicator = (field: SortField) =>
    sortField === field ? (sortDir === "asc" ? " \u2191" : " \u2193") : "";

  return (
    <div className="rounded-xl border bg-card">
      <div className="border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Activations</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Active agency subscriptions
            </p>
          </div>
          {filterField && filterValue && (
            <button
              type="button"
              onClick={clearFilter}
              className="inline-flex items-center gap-1.5 rounded-md border bg-muted/50 px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
            >
              {filterField.toUpperCase()}: {filterValue}
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b text-left">
              <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Agency
              </th>
              <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                AE
              </th>
              <th className="px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                CSM
              </th>
              <SortableHeader
                label="Activation Date"
                field="activated_at"
                current={sortField}
                indicator={sortIndicator("activated_at")}
                onToggle={toggleSort}
              />
              <SortableHeader
                label="CloudStudio Qty"
                field="quantity"
                current={sortField}
                indicator={sortIndicator("quantity")}
                onToggle={toggleSort}
                align="right"
              />
              <SortableHeader
                label="CloudStudio MRR"
                field="mrr_cents"
                current={sortField}
                indicator={sortIndicator("mrr_cents")}
                onToggle={toggleSort}
                align="right"
              />
              <SortableHeader
                label="Total MRR"
                field="total_mrr_cents"
                current={sortField}
                indicator={sortIndicator("total_mrr_cents")}
                onToggle={toggleSort}
                align="right"
              />
              <SortableHeader
                label="Upsell %"
                field="upsell_pct"
                current={sortField}
                indicator={sortIndicator("upsell_pct")}
                onToggle={toggleSort}
                align="right"
              />
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-16 text-center text-sm text-muted-foreground"
                >
                  {filterField
                    ? "No activations match this filter."
                    : "No activations yet. Connect Stripe and run a sync from Settings."}
                </td>
              </tr>
            ) : (
              sorted.map((a) => (
                <tr
                  key={a.stripe_subscription_id}
                  className="border-b last:border-b-0 hover:bg-slate-50/60"
                >
                  <td className="px-6 py-3 text-sm font-medium">
                    {a.agency_name ? (
                      <a
                        href={customerUrl(a.stripe_customer_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        {a.agency_name}
                      </a>
                    ) : (
                      <a
                        href={customerUrl(a.stripe_customer_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground underline-offset-4 hover:underline"
                      >
                        {a.stripe_customer_id}
                      </a>
                    )}
                  </td>
                  <td className="px-6 py-3 text-sm">
                    <FilterableCell
                      value={a.ae}
                      field="ae"
                      activeField={filterField}
                      activeValue={filterValue}
                      onFilter={applyFilter}
                    />
                  </td>
                  <td className="px-6 py-3 text-sm">
                    <FilterableCell
                      value={a.csm}
                      field="csm"
                      activeField={filterField}
                      activeValue={filterValue}
                      onFilter={applyFilter}
                    />
                  </td>
                  <td className="px-6 py-3 text-sm text-muted-foreground">
                    {formatDate(a.activated_at)}
                  </td>
                  <td className="px-6 py-3 text-right text-sm tabular-nums">
                    {a.quantity}
                  </td>
                  <td className="px-6 py-3 text-right text-sm tabular-nums">
                    {formatCurrency(a.mrr_cents)}
                  </td>
                  <td className="px-6 py-3 text-right text-sm tabular-nums">
                    {formatCurrency(a.total_mrr_cents)}
                  </td>
                  <td className="px-6 py-3 text-right text-sm tabular-nums">
                    {(() => {
                      const base = a.total_mrr_cents - a.mrr_cents;
                      if (base <= 0) return "—";
                      return `${((a.total_mrr_cents / base) * 100).toFixed(1)}%`;
                    })()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FilterableCell({
  value,
  field,
  activeField,
  activeValue,
  onFilter,
}: {
  value: string | null;
  field: FilterField;
  activeField: FilterField | null;
  activeValue: string | null;
  onFilter: (field: FilterField, value: string) => void;
}) {
  if (!value) return <span className="text-muted-foreground">—</span>;

  const isActive = activeField === field && activeValue === value;

  return (
    <button
      type="button"
      onClick={() => onFilter(field, value)}
      className={`rounded px-1.5 py-0.5 text-left transition-colors ${
        isActive
          ? "bg-primary/10 font-medium text-primary"
          : "hover:bg-muted hover:text-foreground"
      }`}
    >
      {value}
    </button>
  );
}

function SortableHeader({
  label,
  field,
  current,
  indicator,
  onToggle,
  align,
}: {
  label: string;
  field: SortField;
  current: SortField;
  indicator: string;
  onToggle: (f: SortField) => void;
  align?: "right";
}) {
  return (
    <th
      className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground ${align === "right" ? "text-right" : ""}`}
    >
      <button
        type="button"
        onClick={() => onToggle(field)}
        className={`inline-flex items-center gap-1 hover:text-foreground ${field === current ? "text-foreground" : ""}`}
      >
        {label}
        {indicator}
        {field !== current && (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </button>
    </th>
  );
}
